import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { updateAccountUsername } from "../lib/account-actions";
import { useAuth } from "./useAuth";
import { useCheckUsername } from "./useOnboardingApi";

export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;
type StatusTone = "neutral" | "success" | "error";

export type InlineStatus = {
  tone: StatusTone;
  message: string;
};

function toText(
  t: TranslateFn,
  key: string,
  options?: Record<string, unknown>,
) {
  return String(t(key, options));
}

export function useUsernameEditor(t: TranslateFn) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // A public Tabbio handle is not a UAE PASS identity attribute. It remains
  // editable even when the account's legal name/email are provider-locked.
  const isIdentityLocked = false;
  const identityLockMessage = toText(t, "settings.accountEdit.uaePassLocked");
  const userUsername = (user?.username ?? "").trim().toLowerCase();

  const [usernameBase, setUsernameBase] = useState<string>(userUsername);
  const [username, setUsername] = useState<string>(userUsername);
  const [usernameResult, setUsernameResult] = useState<InlineStatus | null>(
    null,
  );
  const [isSavingUsername, setIsSavingUsername] = useState(false);

  const normalizedUsername = username.trim().toLowerCase();
  const usernameChanged = normalizedUsername !== usernameBase;
  const usernameCheck = useCheckUsername(normalizedUsername);

  useEffect(() => {
    setUsernameBase(userUsername);
    setUsername((prev) => (prev.trim().length > 0 ? prev : userUsername));
  }, [userUsername]);

  useEffect(() => {
    setUsernameResult(null);
  }, [username]);

  const usernameStatus = useMemo<InlineStatus>(() => {
    if (isIdentityLocked) {
      return {
        tone: "neutral",
        message: identityLockMessage,
      };
    }

    if (usernameResult) {
      return usernameResult;
    }

    if (!normalizedUsername) {
      return {
        tone: "neutral",
        message: toText(t, "settings.accountEdit.usernameRequired"),
      };
    }

    if (!usernameChanged) {
      return {
        tone: "neutral",
        message: toText(t, "settings.accountEdit.usernameCurrent"),
      };
    }

    if (normalizedUsername.length < 3) {
      return {
        tone: "error",
        message: toText(t, "settings.accountEdit.usernameMin"),
      };
    }

    if (usernameCheck.isChecking) {
      return {
        tone: "neutral",
        message: toText(t, "settings.accountEdit.usernameChecking"),
      };
    }

    if (usernameCheck.isAvailable === true) {
      return {
        tone: "success",
        message: toText(t, "settings.accountEdit.usernameAvailable"),
      };
    }

    if (usernameCheck.isAvailable === false) {
      if (usernameCheck.reason === "FORMAT_INVALID") {
        return {
          tone: "error",
          message: toText(t, "settings.accountEdit.usernameInvalid"),
        };
      }

      if (usernameCheck.reason === "RESERVED") {
        if (usernameCheck.suggestion) {
          return {
            tone: "error",
            message: toText(
              t,
              "settings.accountEdit.usernameReservedWithSuggestion",
              {
                suggestion: usernameCheck.suggestion,
              },
            ),
          };
        }
        return {
          tone: "error",
          message: toText(t, "settings.accountEdit.usernameReserved"),
        };
      }

      if (usernameCheck.suggestion) {
        return {
          tone: "error",
          message: toText(
            t,
            "settings.accountEdit.usernameTakenWithSuggestion",
            {
              suggestion: usernameCheck.suggestion,
            },
          ),
        };
      }

      return {
        tone: "error",
        message: toText(t, "settings.accountEdit.usernameTaken"),
      };
    }

    return {
      tone: "neutral",
      message: toText(t, "settings.accountEdit.usernameHint"),
    };
  }, [
    normalizedUsername,
    t,
    usernameChanged,
    usernameCheck.isAvailable,
    usernameCheck.isChecking,
    usernameCheck.reason,
    usernameCheck.suggestion,
    usernameResult,
    isIdentityLocked,
    identityLockMessage,
  ]);

  const canSaveUsername =
    !isIdentityLocked &&
    usernameChanged &&
    normalizedUsername.length >= 3 &&
    usernameCheck.isAvailable === true &&
    !usernameCheck.isChecking &&
    !isSavingUsername;

  const saveUsername = useCallback(async (): Promise<string | null> => {
    if (isIdentityLocked) {
      setUsernameResult({
        tone: "neutral",
        message: identityLockMessage,
      });
      return null;
    }

    if (!canSaveUsername) {
      setUsernameResult({
        tone: "error",
        message: toText(t, "settings.accountEdit.usernameCannotSave"),
      });
      return null;
    }

    setIsSavingUsername(true);
    try {
      await updateAccountUsername(normalizedUsername);
      setUsernameBase(normalizedUsername);
      setUsername(normalizedUsername);
      setUsernameResult({
        tone: "success",
        message: toText(t, "settings.accountEdit.usernameUpdated"),
      });
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      return normalizedUsername;
    } catch (error) {
      setUsernameResult({
        tone: "error",
        message:
          error instanceof Error
            ? error.message
            : toText(t, "settings.accountEdit.errorGeneric"),
      });
      return null;
    } finally {
      setIsSavingUsername(false);
    }
  }, [
    canSaveUsername,
    identityLockMessage,
    isIdentityLocked,
    normalizedUsername,
    queryClient,
    t,
  ]);

  return {
    /** The saved handle the input is compared against. */
    currentUsername: usernameBase,
    username,
    setUsername,
    normalizedUsername,
    usernameChanged,
    usernameStatus,
    canSaveUsername,
    saveUsername,
    isSavingUsername,
    isIdentityLocked,
    identityLockMessage,
  };
}
